/**
 * AdminBookRideModal.jsx
 *
 * Admin panel-ல phone call வந்தப்போ இந்த modal திறக்கும்.
 * Steps:
 *   1. Passenger details (name, mobile)
 *   2. Pickup + Drop location (address + coords)
 *   3. Fare estimate + Payment method
 *   4. Nearest online drivers list → select
 *   5. Confirm booking → ride created + OTP sent
 *   6. Live status (waiting → accepted → OTP verified → trip started)
 *
 * Usage:
 *   <AdminBookRideModal isOpen={open} onClose={() => setOpen(false)} socket={socket} />
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getNearbyDrivers,
  createAdminBooking,
  getBookingStatus,
  cancelAdminBooking,
} from "../api/bookRideByAdminApi";
import { GoogleMap, Marker, DirectionsRenderer } from "@react-google-maps/api";

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 1200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    backdropFilter: "blur(3px)",
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    width: "100%",
    maxWidth: 560,
    maxHeight: "92vh",
    overflowY: "auto",
    boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
    fontFamily: "'Segoe UI', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px 0",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1a1a2e",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 22,
    cursor: "pointer",
    color: "#666",
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: 6,
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    padding: "16px 24px",
    borderBottom: "1px solid #f0f0f0",
  },
  stepItem: (active, done) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
  }),
  stepDot: (active, done) => ({
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    background: done ? "#16a34a" : active ? "#2563eb" : "#e5e7eb",
    color: done || active ? "#fff" : "#9ca3af",
    flexShrink: 0,
    transition: "all 0.2s",
  }),
  stepLabel: (active, done) => ({
    fontSize: 11,
    fontWeight: active ? 600 : 400,
    color: done ? "#16a34a" : active ? "#2563eb" : "#9ca3af",
    display: window.innerWidth > 420 ? "block" : "none",
  }),
  stepLine: (done) => ({
    flex: 1,
    height: 2,
    background: done ? "#16a34a" : "#e5e7eb",
    maxWidth: 24,
    transition: "background 0.3s",
  }),
  body: { padding: "20px 24px 24px" },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1.5px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    color: "#1f2937",
    outline: "none",
    boxSizing: "border-box",
    transition: "border 0.15s",
  },
  row: { display: "flex", gap: 12, marginBottom: 16 },
  col: { flex: 1 },
  fareBox: {
    background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)",
    borderRadius: 12,
    padding: "16px 20px",
    color: "#fff",
    marginBottom: 20,
  },
  fareAmount: { fontSize: 32, fontWeight: 800, lineHeight: 1 },
  fareLabel: { fontSize: 12, opacity: 0.8, marginTop: 2 },
  payRow: { display: "flex", gap: 10, marginBottom: 20 },
  payBtn: (active) => ({
    flex: 1,
    padding: "10px 8px",
    borderRadius: 10,
    border: active ? "2px solid #2563eb" : "1.5px solid #e5e7eb",
    background: active ? "#eff6ff" : "#fff",
    color: active ? "#1d4ed8" : "#374151",
    fontWeight: active ? 700 : 500,
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.15s",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  }),
  driverCard: (selected) => ({
    border: selected ? "2px solid #2563eb" : "1.5px solid #e5e7eb",
    borderRadius: 12,
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    background: selected ? "#eff6ff" : "#fff",
    transition: "all 0.15s",
    marginBottom: 10,
  }),
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    background: "#e5e7eb",
    flexShrink: 0,
  },
  driverName: { fontSize: 14, fontWeight: 700, color: "#1f2937" },
  driverSub: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  badge: (color) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: color === "green" ? "#dcfce7" : "#dbeafe",
    color: color === "green" ? "#16a34a" : "#1d4ed8",
  }),
  primaryBtn: (disabled) => ({
    width: "100%",
    padding: "13px",
    background: disabled
      ? "#9ca3af"
      : "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    marginTop: 8,
  }),
  secondaryBtn: {
    width: "100%",
    padding: "11px",
    background: "#f3f4f6",
    color: "#374151",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
  },
  statusBox: (color) => ({
    background:
      color === "green"
        ? "#f0fdf4"
        : color === "blue"
          ? "#eff6ff"
          : color === "amber"
            ? "#fffbeb"
            : "#f9fafb",
    border: `1.5px solid ${
      color === "green"
        ? "#86efac"
        : color === "blue"
          ? "#93c5fd"
          : color === "amber"
            ? "#fcd34d"
            : "#e5e7eb"
    }`,
    borderRadius: 12,
    padding: "16px 20px",
    textAlign: "center",
    marginBottom: 16,
  }),
  otpDisplay: {
    fontSize: 36,
    fontWeight: 900,
    letterSpacing: 10,
    color: "#1d4ed8",
    fontFamily: "monospace",
  },
  errorText: {
    fontSize: 12,
    color: "#dc2626",
    marginTop: 4,
  },
  spinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginRight: 8,
    verticalAlign: "middle",
  },
};

const STEPS = ["Passenger", "Location", "Driver", "Confirm"];

// ─────────────────────────────────────────────────────────────────────────────
// SmartLocationInput — defined OUTSIDE main component to avoid hooks-count error
// ─────────────────────────────────────────────────────────────────────────────
const SmartLocationInput = ({ label, value, onChange, field, onOpenPin }) => {
  const inputRef = useRef(null);
  const acInstanceRef = useRef(null);
  const latestOnChange = useRef(onChange);
  latestOnChange.current = onChange;

  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;
    if (acInstanceRef.current) return; // already initialised
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "in" },
      fields: ["formatted_address", "geometry"],
    });
    acInstanceRef.current = ac;
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      onChange({
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
      });
    });
    // Cleanup
    return () => {
      if (acInstanceRef.current) {
        window.google.maps.event.clearInstanceListeners(acInstanceRef.current);
        acInstanceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isSet = !!(value.latitude && value.longitude);

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={S.label}>{label}</label>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            ref={inputRef}
            style={{
              ...S.input,
              borderColor: isSet ? "#16a34a" : "#d1d5db",
            }}
            placeholder="Type to search location..."
            value={value.address}
            onChange={(e) =>
              onChange({ address: e.target.value, latitude: "", longitude: "" })
            }
            onFocus={(e) =>
              (e.target.style.borderColor = isSet ? "#16a34a" : "#2563eb")
            }
            onBlur={(e) =>
              (e.target.style.borderColor = isSet ? "#16a34a" : "#d1d5db")
            }
          />
          {isSet && (
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 14,
                pointerEvents: "none",
              }}
            >
              ✅
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onOpenPin(field)}
          style={{
            flexShrink: 0,
            padding: "0 14px",
            background: isSet ? "#dcfce7" : "#eff6ff",
            border: `1.5px solid ${isSet ? "#16a34a" : "#2563eb"}`,
            borderRadius: 8,
            color: isSet ? "#16a34a" : "#1d4ed8",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          📍 Pin
        </button>
      </div>
      {isSet && (
        <div style={{ fontSize: 11, color: "#16a34a", marginTop: 4 }}>
          ✓ {parseFloat(value.latitude).toFixed(5)},{" "}
          {parseFloat(value.longitude).toFixed(5)}
        </div>
      )}
    </div>
  );
};

// Road route preview. Uses Google Directions; no straight-line distance guessing.
const RoutePreview = ({ pickup, drop, fareInfo, selectedDriverFare, onRouteInfo }) => {
  const [directions, setDirections] = useState(null);
  const [routeError, setRouteError] = useState("");
  const [working, setWorking] = useState(false);
  const lat1 = Number(pickup.latitude), lng1 = Number(pickup.longitude);
  const lat2 = Number(drop.latitude), lng2 = Number(drop.longitude);
  const valid = pickup.latitude !== "" && pickup.longitude !== "" &&
    drop.latitude !== "" && drop.longitude !== "" &&
    [lat1, lng1, lat2, lng2].every(Number.isFinite);
  useEffect(() => {
    if (!valid) { setDirections(null); onRouteInfo(null); return; }
    if (!window.google?.maps?.DirectionsService) {
      setRouteError("Google Maps Directions is not loaded"); return;
    }
    let active = true;
    setWorking(true);
    setRouteError("");
    setDirections(null);
    onRouteInfo(null);
    const service = new window.google.maps.DirectionsService();
    service.route({
      origin: { lat: lat1, lng: lng1 },
      destination: { lat: lat2, lng: lng2 },
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (!active) return;
      setWorking(false);
      if (status === "OK" && result?.routes?.[0]?.legs?.[0]) {
        const leg = result.routes[0].legs[0];
        setDirections(result);
        onRouteInfo({ distanceKm: leg.distance.value / 1000, duration: leg.duration.text });
      } else {
        setRouteError(`Road route unavailable (${status}). Check Maps Directions API access.`);
        onRouteInfo(null);
      }
    });
    return () => { active = false; };
  }, [valid, lat1, lng1, lat2, lng2, onRouteInfo]);
  if (!valid) return null;
  const center = { lat: lat1, lng: lng1 };
  return (
    <div style={{ border: "1px solid #dbeafe", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "10px 12px", fontWeight: 700, color: "#1d4ed8" }}>🗺️ Pickup → Drop Road Route</div>
      <GoogleMap mapContainerStyle={{ width: "100%", height: "240px" }} center={center} zoom={12}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}>
        {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: false }} />}
        {!directions && <Marker position={center} />}
      </GoogleMap>
      <div style={{ padding: "10px 12px", fontSize: 13 }}>
        {working ? "Calculating road route..." : routeError ? <span style={{ color: "#b91c1c" }}>{routeError}</span> :
          directions ? <span>Road route ready · {directions.routes[0].legs[0].distance.text} · {directions.routes[0].legs[0].duration.text}</span> : null}
        <div style={{ marginTop: 5, color: "#374151" }}>
          {selectedDriverFare != null ? <strong>Selected driver fare: ₹{selectedDriverFare}</strong> :
            fareInfo?.minimumFare != null ? <span>Minimum fare: ₹{fareInfo.minimumFare} (final fare depends on driver)</span> :
            <span>Final fare will appear after selecting a driver.</span>}
        </div>
      </div>
    </div>
  );
};

// Only explicit backend capability flags can establish payment compatibility.
const supportsPayment = (driver, mode) => {
  const modes = driver.paymentModes || driver.acceptedPaymentModes || driver.supportedPaymentModes;
  if (Array.isArray(modes)) return modes.map(x => String(x).toLowerCase()).includes(mode);
  if (mode === "cash" && typeof driver.acceptsCash === "boolean") return driver.acceptsCash;
  if (mode === "online" && typeof driver.acceptsOnline === "boolean") return driver.acceptsOnline;
  return null; // Unknown: don't claim driver accepts a payment method.
};

// Map search: select a Google Places suggestion to move the marker automatically.
const MapPlaceSearch = ({ onSelect }) => {
  const inputRef = useRef(null);
  const callbackRef = useRef(onSelect);
  callbackRef.current = onSelect;
  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places) return;
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "in" },
      fields: ["formatted_address", "name", "geometry"],
    });
    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry?.location) return;
      callbackRef.current({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: place.formatted_address || place.name || "",
      });
    });
    return () => listener.remove();
  }, []);
  return (
    <div style={{ padding: "12px 18px", background: "#fff", flexShrink: 0 }}>
      <input ref={inputRef} style={S.input}
        placeholder="🔎 Search area, street, landmark or address..."
        aria-label="Search map location" autoComplete="off" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MapPinModal — defined OUTSIDE main component, receives everything via props
// ─────────────────────────────────────────────────────────────────────────────
const MapPinModal = ({
  field,
  center,
  pinPos,
  pinAddress,
  reverseLoading,
  onMapClick,
  onSearchSelect,
  onConfirm,
  onClose,
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 1400,
      background: "rgba(0,0,0,0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        width: "100%",
        maxWidth: 700,
        height: "92vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          borderBottom: "1px solid #f0f0f0",
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>
          📍 {field === "pickup" ? "Pickup" : "Drop"} Location Pin
        </div>
        <button style={S.closeBtn} onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Instruction */}
      <div
        style={{
          padding: "10px 18px",
          background: "#eff6ff",
          fontSize: 12,
          color: "#1d4ed8",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        🗺️ Map-ல எங்கும் tap/click பண்ணு → exact location pin drop ஆகும்.
        Marker-ஐ drag பண்ணவும் செய்யலாம்.
      </div>

      <MapPlaceSearch onSelect={onSearchSelect} />

      {/* Map */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={14}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {pinPos && (
            <Marker
              position={pinPos}
              draggable={true}
              onDragEnd={onMapClick}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          )}
        </GoogleMap>
        {!pinPos && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                borderRadius: 20,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              👆 Tap anywhere to drop pin
            </div>
          </div>
        )}
      </div>

      {/* Address preview */}
      <div
        style={{
          padding: "12px 18px",
          borderTop: "1px solid #f0f0f0",
          minHeight: 52,
          flexShrink: 0,
        }}
      >
        {reverseLoading ? (
          <div
            style={{
              color: "#6b7280",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                border: "2px solid #d1d5db",
                borderTopColor: "#2563eb",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            Getting address...
          </div>
        ) : pinAddress ? (
          <div style={{ fontSize: 13, color: "#1f2937" }}>
            <span style={{ color: "#16a34a", fontWeight: 600 }}>📍 </span>
            {pinAddress}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "#9ca3af" }}>
            Address will appear here after pin drop
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: "0 18px 16px",
          display: "flex",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <button
          style={{
            ...S.primaryBtn(!pinPos || reverseLoading),
            margin: 0,
            flex: 1,
          }}
          disabled={!pinPos || reverseLoading}
          onClick={onConfirm}
        >
          ✅ Confirm This Location
        </button>
        <button
          style={{ ...S.secondaryBtn, margin: 0, flex: 1 }}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminBookRideModal({ isOpen, onClose, socket }) {
  const admin = localStorage.getItem("admin");

  console.log(admin, "<<<< admin local storage");

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form fields
  const [passengerName, setPassengerName] = useState("");
  const [passengerMobile, setPassengerMobile] = useState("");
  const [passengerGender, setPassengerGender] = useState("");

  const [pickup, setPickup] = useState({
    address: "",
    latitude: "",
    longitude: "",
  });
  const [drop, setDrop] = useState({
    address: "",
    latitude: "",
    longitude: "",
  });

  // Extracted from pickup reverse geocode
  const [locationMeta, setLocationMeta] = useState({ state: "", city: "" });

  const [fareInfo, setFareInfo] = useState(null);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [routeInfo, setRouteInfo] = useState(null);

  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  // ✅ Driver location base panni recalculate pannina fare (PassengerMap pattern)
  const [selectedDriverFare, setSelectedDriverFare] = useState(null);

  const [booking, setBooking] = useState(null);
  const [rideStatus, setRideStatus] = useState("");

  const statusPollRef = useRef(null);

  // ── Map Pin state (always declared — no conditional hooks) ──────────────────
  const [mapPinOpen, setMapPinOpen] = useState(null); // "pickup" | "drop" | null
  const [mapPinCenter, setMapPinCenter] = useState(() => {
    // Lazy init — read zone coords from localStorage at mount time
    try {
      const _a = localStorage.getItem("admin");
      const _d = _a ? JSON.parse(_a) : null;
      const _c = _d?.zone?.location?.coordinates;
      return { lat: _c?.lat || 11.0168, lng: _c?.lng || 76.9558 };
    } catch {
      return { lat: 11.0168, lng: 76.9558 };
    }
  });
  const [mapPinPos, setMapPinPos] = useState(null);
  const [mapPinAddress, setMapPinAddress] = useState("");
  const [reverseLoading, setReverseLoading] = useState(false);
  const geocodeRequestRef = useRef(0);

  // ── Reset on close ──────────────────────────────────────────────────────────
  const resetAll = useCallback(() => {
    setStep(0);
    setLoading(false);
    setError("");
    setPassengerName("");
    setPassengerMobile("");
    setPassengerGender("");
    setPickup({ address: "", latitude: "", longitude: "" });
    setDrop({ address: "", latitude: "", longitude: "" });
    setLocationMeta({ state: "", city: "" });
    setFareInfo(null);
    setRouteInfo(null);
    setPaymentMode("cash");
    setNearbyDrivers([]);
    setSelectedDriver(null);
    setSelectedDriverFare(null);
    setBooking(null);
    setRideStatus("");
    setMapPinOpen(null);
    if (statusPollRef.current) clearInterval(statusPollRef.current);
  }, []);

  // Close modal WITHOUT resetting state.
  // Ride active-aa irukum pothu close panninaa state survive aaganum.
  // resetAll() only trip_started / cancelled / "New Booking" click-la mattum.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Full reset — only when ride is done or user starts fresh
  const handleFullReset = useCallback(() => {
    resetAll();
    onClose();
  }, [resetAll, onClose]);

  // ── Socket: ride status updates ─────────────────────────────────────────────
  useEffect(() => {
    if (!socket || !booking) return;
    const onAccepted = (data) => {
      if (data.rideId === booking.rideId || data.driverId === booking.driverId)
        setRideStatus("accepted");
    };
    const onTripStarted = (data) => {
      if (data.rideId === booking.rideId) {
        setRideStatus("trip_started");
        if (statusPollRef.current) clearInterval(statusPollRef.current);
      }
    };
    const onAdminStarted = (data) => {
      if (data.rideId === booking.rideId) setRideStatus("trip_started");
    };
    const onCancelled = (data) => {
      if (data.rideId === booking.rideId) setRideStatus("cancelled");
    };
    socket.on("ride-accepted", onAccepted);
    socket.on("trip_started", onTripStarted);
    socket.on("admin-ride-started", onAdminStarted);
    socket.on("ride-cancelled", onCancelled);
    return () => {
      socket.off("ride-accepted", onAccepted);
      socket.off("trip_started", onTripStarted);
      socket.off("admin-ride-started", onAdminStarted);
      socket.off("ride-cancelled", onCancelled);
    };
  }, [socket, booking]);

  // ── Poll ride status (fallback) ─────────────────────────────────────────────
  useEffect(() => {
    if (!booking?.rideId || rideStatus === "trip_started" || !isOpen) return;
    statusPollRef.current = setInterval(async () => {
      try {
        const res = await getBookingStatus(booking.rideId);
        if (res.success) {
          setRideStatus(res.ride.status);
          if (res.ride.status === "trip_started")
            clearInterval(statusPollRef.current);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(statusPollRef.current);
  }, [booking, rideStatus, isOpen]);

  // ── Reverse geocode — returns address string ────────────────────────────────
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      return await new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          resolve(
            status === "OK" && results[0]
              ? results[0].formatted_address
              : `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          );
        });
      });
    } catch {
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
  }, []);

  // ── Extract state + city from geocode results ─────────────────────────────
  // Google address_components-la "administrative_area_level_1" = state,
  // "administrative_area_level_2" / "locality" = city
  const extractStateCityFromCoords = useCallback(async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      return await new Promise((resolve) => {
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status !== "OK" || !results[0]) {
            return resolve({ state: "Tamil Nadu", city: "Salem" });
          }
          let state = "";
          let city = "";
          for (const comp of results[0].address_components) {
            if (comp.types.includes("administrative_area_level_1")) {
              state = comp.long_name;
            }
            if (
              comp.types.includes("locality") ||
              comp.types.includes("administrative_area_level_2")
            ) {
              if (!city) city = comp.long_name; // locality first preference
            }
          }
          resolve({
            state: state || "Tamil Nadu",
            city: city || "Salem",
          });
        });
      });
    } catch {
      return { state: "Tamil Nadu", city: "Salem" };
    }
  }, []);

  // ── Map pin handlers ────────────────────────────────────────────────────────
  const openMapPin = useCallback(
    (field) => {
      geocodeRequestRef.current += 1;
      setReverseLoading(false);
      const current = field === "pickup" ? pickup : drop;
      if (current.latitude && current.longitude) {
        setMapPinCenter({
          lat: parseFloat(current.latitude),
          lng: parseFloat(current.longitude),
        });
        setMapPinPos({
          lat: parseFloat(current.latitude),
          lng: parseFloat(current.longitude),
        });
        setMapPinAddress(current.address);
      } else {
        // Use logged-in user's zone coordinates as map center
        const _admin = localStorage.getItem("admin");
        const _adminData = _admin ? JSON.parse(_admin) : null;
        const _coords = _adminData?.zone?.location?.coordinates;
        setMapPinCenter({
          lat: _coords?.lat || 11.0168,
          lng: _coords?.lng || 76.9558,
        });
        setMapPinPos(null);
        setMapPinAddress("");
      }
      setMapPinOpen(field);
    },
    [pickup, drop],
  );

  const handleMapClick = useCallback(
    async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMapPinPos({ lat, lng });
      const requestId = ++geocodeRequestRef.current;
      setReverseLoading(true);
      const addr = await reverseGeocode(lat, lng);
      if (requestId !== geocodeRequestRef.current) return;
      setMapPinAddress(addr);
      setReverseLoading(false);
    },
    [reverseGeocode],
  );

  const handleMapSearchSelect = useCallback(({ lat, lng, address }) => {
    geocodeRequestRef.current += 1;
    setMapPinCenter({ lat, lng });
    setMapPinPos({ lat, lng });
    setMapPinAddress(address);
    setReverseLoading(false);
  }, []);

  const confirmMapPin = useCallback(async () => {
    if (!mapPinPos) return;
    const val = {
      address: mapPinAddress,
      latitude: mapPinPos.lat,
      longitude: mapPinPos.lng,
    };
    if (mapPinOpen === "pickup") {
      setPickup(val);
      // Extract state/city from pickup coords for passenger DB record
      const meta = await extractStateCityFromCoords(
        mapPinPos.lat,
        mapPinPos.lng,
      );
      setLocationMeta(meta);
      console.log("📍 Location meta from pickup:", meta);
    } else {
      setDrop(val);
    }
    setMapPinOpen(null);
  }, [mapPinPos, mapPinAddress, mapPinOpen, extractStateCityFromCoords]);

  // ── Step navigation ─────────────────────────────────────────────────────────
  const goToLocation = () => {
    if (!passengerName.trim()) return setError("Passenger name required");
    if (!/^[6-9]\d{9}$/.test(passengerMobile))
      return setError("Valid 10-digit mobile number required");
    setError("");
    setStep(1);
  };

  // Driver select pannumbodhu — fare already backend-la (getNearbyDrivers) compute
  // pannirukom (FareConfig DB values + night surge dynamic-ah). Client-side static
  // default vachu recalculate pannuvathu illa, server-oda `driver.fare` nera use pannuvom.
  const onDriverSelect = (driver) => {
    if (supportsPayment(driver, paymentMode) !== true) {
      setError("Driver payment support is not verified for this method. Choose a compatible driver.");
      return;
    }
    setError("");
    setSelectedDriver(driver);
    setSelectedDriverFare(driver.fare ?? null);
  };

  // Location step -> directly nearby drivers + per-driver dynamic fare fetch pannும்
  // (Fare & Pay nu thani step venaam — PassengerMap pattern: driver list-lயே fare kaatuvom)
  const goToDrivers = async () => {
    if (!pickup.address || !pickup.latitude)
      return setError("Pickup location required");
    if (!drop.address || !drop.latitude)
      return setError("Drop location required");
    setError("");
    setLoading(true);
    // If locationMeta not yet extracted (e.g. pickup set via autocomplete, not map pin)
    if (!locationMeta.state && pickup.latitude && pickup.longitude) {
      const meta = await extractStateCityFromCoords(
        parseFloat(pickup.latitude),
        parseFloat(pickup.longitude),
      );
      setLocationMeta(meta);
      console.log("📍 Location meta extracted at goToDrivers:", meta);
    }
    try {
      const res = await getNearbyDrivers({
        pickupLat: pickup.latitude,
        pickupLng: pickup.longitude,
        dropLat: drop.latitude,
        dropLng: drop.longitude,
      });
      if (res.success) {
        // ✅ Ride-level info (distance, per-km rate) — dynamic-ah backend FareConfig-la irundhu
        setFareInfo({
          distanceKm: res.rideDistanceKm,
          perKmFare: res.perKmFare,
          minimumFare: res.minimumFare,
          commission: res.commission,
        });
        setNearbyDrivers(res.drivers);
        setSelectedDriver(null);
        setSelectedDriverFare(null);
        if (res.drivers.length === 0)
          setError("No online drivers nearby. Try again in a moment.");
        else setStep(2);
      } else setError(res.error || "Failed to fetch drivers");
    } catch (err) {
      setError(err.response?.error || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const goToConfirm = () => {
    if (!selectedDriver) return setError("Please select a driver");
    if (supportsPayment(selectedDriver, paymentMode) !== true) return setError("Selected driver does not have verified payment support");
    setError("");
    setStep(3);
  };

  const bookRide = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await createAdminBooking({
        passengerName,
        passengerMobile: `+91${passengerMobile}`,
        passengerGender: passengerGender || "Other",
        passengerState: locationMeta.state || "Tamil Nadu",
        passengerCity: locationMeta.city || "Salem",
        pickup,
        drop,
        driverId: selectedDriver.driverId,
        fare: selectedDriverFare ?? selectedDriver.fare,
        paymentMode,
        bookedBy: "admin",
      });
      if (res.success) {
        console.log(res, "Booking response");

        setBooking(res);
        setRideStatus("requested");
        setStep(4);
      } else setError(res.error || "Booking failed");
    } catch (err) {
      setError(err.response?.error || "Booking failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async () => {
    if (!booking?.rideId) return handleFullReset();
    try {
      await cancelAdminBooking({
        rideId: booking.rideId,
        driverId: selectedDriver?.driverId,
        reason: "Cancelled by admin",
      });
    } catch {}
    // Ride cancelled -> full reset so next booking starts fresh
    handleFullReset();
  };

  // ── Early return AFTER all hooks ────────────────────────────────────────────
  if (!isOpen) return null;

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .arb-modal { animation: fadeIn 0.2s ease; }
        .pac-container { z-index: 99999 !important; }
        .arb-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
      `}</style>

      {/* Map Pin Modal — rendered outside main modal overlay */}
      {mapPinOpen && (
        <MapPinModal
          field={mapPinOpen}
          center={mapPinCenter}
          pinPos={mapPinPos}
          pinAddress={mapPinAddress}
          reverseLoading={reverseLoading}
          onMapClick={handleMapClick}
          onSearchSelect={handleMapSearchSelect}
          onConfirm={confirmMapPin}
          onClose={() => setMapPinOpen(null)}
        />
      )}

      <div
        style={S.overlay}
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <div style={S.modal} className="arb-modal">
          {/* Header */}
          <div style={S.header}>
            <div style={S.title}>
              📞 Admin Phone Booking
              {booking && (
                <span
                  style={{
                    fontSize: 12,
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    padding: "2px 8px",
                    borderRadius: 20,
                    fontWeight: 600,
                  }}
                >
                  #{booking.rideId?.slice(-6)}
                  {booking.otp && ` · OTP: ${booking.otp}`}
                </span>
              )}
            </div>
            <button style={S.closeBtn} onClick={handleClose}>
              ✕
            </button>
          </div>

          {/* Stepper */}
          {step < 5 && (
            <div style={S.stepper}>
              {STEPS.map((label, i) => (
                <React.Fragment key={i}>
                  <div style={S.stepItem(step === i, step > i)}>
                    <div style={S.stepDot(step === i, step > i)}>
                      {step > i ? "✓" : i + 1}
                    </div>
                    <span style={S.stepLabel(step === i, step > i)}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && <div style={S.stepLine(step > i)} />}
                </React.Fragment>
              ))}
            </div>
          )}

          <div style={S.body}>
            {/* ── STEP 0: Passenger Details ────────────────────────── */}
            {step === 0 && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>Passenger Name *</label>
                  <input
                    style={S.input}
                    placeholder="e.g. Ramu Kumar"
                    value={passengerName}
                    onChange={(e) => {
                      const val = e.target.value;
                      const titled = val.replace(/\b\w/g, (c) =>
                        c.toUpperCase(),
                      );
                      setPassengerName(titled);
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>Mobile Number *</label>
                  <input
                    style={S.input}
                    placeholder="10-digit mobile number"
                    value={passengerMobile}
                    onChange={(e) =>
                      setPassengerMobile(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    onFocus={(e) => (e.target.style.borderColor = "#2563eb")}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                    type="tel"
                  />
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    📱 OTP இந்த number-க்கு போகும்
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>
                    Gender{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                      (optional)
                    </span>
                  </label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["Male", "Female", "Other"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setPassengerGender(g)}
                        style={{
                          flex: 1,
                          padding: "9px 8px",
                          borderRadius: 8,
                          border:
                            passengerGender === g
                              ? "2px solid #2563eb"
                              : "1.5px solid #e5e7eb",
                          background:
                            passengerGender === g ? "#eff6ff" : "#fff",
                          color: passengerGender === g ? "#1d4ed8" : "#6b7280",
                          fontWeight: passengerGender === g ? 700 : 400,
                          fontSize: 13,
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {g === "Male"
                          ? "👨 Male"
                          : g === "Female"
                            ? "👩 Female"
                            : "🧑 Other"}
                      </button>
                    ))}
                  </div>
                </div>
                {error && <div style={S.errorText}>⚠ {error}</div>}
                <button
                  style={S.primaryBtn(false)}
                  className="arb-btn"
                  onClick={goToLocation}
                >
                  Next: Set Locations →
                </button>
              </>
            )}

            {/* ── STEP 1: Location ────────────────────────────────── */}
            {step === 1 && (
              <>
                <SmartLocationInput
                  label="📍 Pickup Location *"
                  value={pickup}
                  onChange={setPickup}
                  field="pickup"
                  onOpenPin={openMapPin}
                />
                <SmartLocationInput
                  label="🏁 Drop Location *"
                  value={drop}
                  onChange={setDrop}
                  field="drop"
                  onOpenPin={openMapPin}
                />
                <RoutePreview pickup={pickup} drop={drop} fareInfo={fareInfo} selectedDriverFare={selectedDriverFare} onRouteInfo={setRouteInfo} />
                {error && <div style={S.errorText}>⚠ {error}</div>}
                <button
                  style={S.primaryBtn(loading)}
                  className="arb-btn"
                  onClick={goToDrivers}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span style={S.spinner} />
                      Finding drivers...
                    </>
                  ) : (
                    "Next: Find Nearby Drivers →"
                  )}
                </button>
                <button
                  style={S.secondaryBtn}
                  onClick={() => {
                    setError("");
                    setStep(0);
                  }}
                >
                  ← Back
                </button>
              </>
            )}

            {/* ── STEP 2: Driver Selection (dynamic per-driver fare + payment) ── */}
            {step === 2 && (
              <>
                {fareInfo?.distanceKm != null && (
                  <div style={S.fareBox}>
                    <div
                      style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}
                    >
                      Ride Distance
                    </div>
                    <div style={S.fareAmount}>{fareInfo.distanceKm} km</div>
                    <div style={S.fareLabel}>
                      Min ₹{fareInfo.minimumFare} · ₹{fareInfo.perKmFare}/km
                      {" · "}Fare kீழe ovoru driver-ku vera vera (driver→pickup
                      dooram base panni)
                    </div>
                  </div>
                )}
                <RoutePreview pickup={pickup} drop={drop} fareInfo={fareInfo} selectedDriverFare={selectedDriverFare} onRouteInfo={setRouteInfo} />
                <label style={S.label}>Passenger Payment Method *</label>
                <div style={S.payRow}>
                  <button
                    style={S.payBtn(paymentMode === "cash")}
                    onClick={() => { setPaymentMode("cash"); setSelectedDriver(null); setSelectedDriverFare(null); }}
                  >
                    <span style={{ fontSize: 20 }}>💵</span>
                    <span>Cash</span>
                  </button>
                  <button
                    style={S.payBtn(paymentMode === "online")}
                    onClick={() => { setPaymentMode("online"); setSelectedDriver(null); setSelectedDriverFare(null); }}
                  >
                    <span style={{ fontSize: 20 }}>📲</span>
                    <span>Online</span>
                  </button>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#6b7280",
                    margin: "14px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>🛺 {nearbyDrivers.filter(driver => supportsPayment(driver, paymentMode) === true).length} verified {paymentMode} drivers</span>
                  <button
                    style={{
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                      color: "#374151",
                    }}
                    onClick={goToDrivers}
                  >
                    🔄 Refresh
                  </button>
                </div>
                {nearbyDrivers.filter(driver => supportsPayment(driver, paymentMode) === true).length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "24px 0",
                      color: "#6b7280",
                    }}
                  >
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🛺</div>
                    <div>No drivers with verified {paymentMode} support available.</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>Check driver bank verification and nearby-drivers payment flags.</div>
                  </div>
                ) : (
                  nearbyDrivers.filter(driver => supportsPayment(driver, paymentMode) === true).map((driver) => (
                    <div
                      key={driver.driverId}
                      style={S.driverCard(
                        selectedDriver?.driverId === driver.driverId,
                      )}
                      onClick={() => onDriverSelect(driver)}
                    >
                      <img
                        src={driver.photo}
                        alt={driver.name}
                        style={S.avatar}
                        onError={(e) => {
                          e.target.src =
                            "https://ui-avatars.com/api/?name=" +
                            encodeURIComponent(driver.name);
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={S.driverName}>{driver.name}</div>
                        <div style={S.driverSub}>
                          {driver.vehicleNumber} · ⭐ {driver.avgRating}
                        </div>
                        <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
                          <span style={S.badge("green")}>
                            {driver.distanceKm} km away
                          </span>
                          <span style={S.badge("blue")}>online</span>
                          <span style={S.badge("green")}>{paymentMode === "cash" ? "💵 Cash accepted" : "📲 Online accepted"}</span>
                        </div>
                      </div>
                      {/* ✅ Backend already compute pannitu anuppichi fare (driver→pickup +
                          pickup→drop + night surge dynamic-ah FareConfig DB-la irundhu) */}
                      {driver.fare != null && (
                        <div style={{ textAlign: "right", marginRight: 8 }}>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: 15,
                              color: "#419952",
                            }}
                          >
                            ₹{driver.fare}
                          </div>
                          {driver.isNightSurge && (
                            <div style={{ fontSize: 10, color: "#b45309" }}>
                              🌙 surge +₹{driver.nightSurgeAmount}
                            </div>
                          )}
                        </div>
                      )}
                      {selectedDriver?.driverId === driver.driverId && (
                        <div style={{ fontSize: 20, color: "#2563eb" }}>✓</div>
                      )}
                    </div>
                  ))
                )}
                {error && <div style={S.errorText}>⚠ {error}</div>}
                {nearbyDrivers.filter(driver => supportsPayment(driver, paymentMode) === true).length > 0 && (
                  <button
                    style={S.primaryBtn(!selectedDriver)}
                    className="arb-btn"
                    onClick={goToConfirm}
                    disabled={!selectedDriver}
                  >
                    Next: Confirm Booking →
                  </button>
                )}
                <button
                  style={S.secondaryBtn}
                  onClick={() => {
                    setError("");
                    setStep(1);
                  }}
                >
                  ← Back
                </button>
              </>
            )}

            {/* ── STEP 3: Confirm Summary ──────────────────────────── */}
            {step === 3 && selectedDriver && fareInfo && (
              <>
                <div
                  style={{
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "16px 18px",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: "#6b7280",
                      marginBottom: 12,
                      fontWeight: 600,
                    }}
                  >
                    Booking Summary
                  </div>
                  {[
                    ["👤 Passenger", `${passengerName} · ${passengerMobile}`],
                    ["📍 Pickup", pickup.address],
                    ["🏁 Drop", drop.address],
                    ["🛣️ Road route", routeInfo ? `${routeInfo.distanceKm.toFixed(2)} km · ${routeInfo.duration}` : "Not available"],
                    [
                      "💰 Fare",
                      `₹${selectedDriverFare ?? selectedDriver.fare} (${fareInfo.distanceKm} km)`,
                    ],
                    [
                      "💳 Payment",
                      paymentMode === "cash" ? "💵 Cash" : "📲 Online",
                    ],
                    [
                      "🛺 Driver",
                      `${selectedDriver.name} · ${selectedDriver.vehicleNumber}`,
                    ],
                    [
                      "⭐ Rating",
                      `${selectedDriver.avgRating} · ${selectedDriver.distanceKm} km away`,
                    ],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        display: "flex",
                        gap: 10,
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: "#6b7280", minWidth: 110 }}>
                        {k}
                      </span>
                      <span
                        style={{ color: "#1f2937", fontWeight: 500, flex: 1 }}
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Passenger DB status badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#f0fdf4",
                    border: "1px solid #86efac",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#166534",
                    marginBottom: 12,
                  }}
                >
                  <span style={{ fontSize: 16 }}>✅</span>
                  <span>
                    Passenger will be <strong>auto-verified</strong> in DB —
                    existing record reused if phone already registered
                  </span>
                </div>

                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontSize: 13,
                    color: "#92400e",
                    marginBottom: 16,
                  }}
                >
                  📱 OTP will be sent to <strong>{passengerMobile}</strong> via
                  SMS
                </div>
                {error && <div style={S.errorText}>⚠ {error}</div>}
                <button
                  style={S.primaryBtn(loading)}
                  className="arb-btn"
                  onClick={bookRide}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span style={S.spinner} />
                      Booking ride...
                    </>
                  ) : (
                    "✅ Confirm & Book Ride"
                  )}
                </button>
                <button
                  style={S.secondaryBtn}
                  onClick={() => {
                    setError("");
                    setStep(2);
                  }}
                >
                  ← Change Driver
                </button>
              </>
            )}

            {/* ── STEP 4: Booking Success + Live Status ─────────────── */}
            {step === 4 && booking && (
              <>
                <div style={{ ...S.statusBox("blue"), marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#1d4ed8",
                      fontWeight: 600,
                      marginBottom: 6,
                    }}
                  >
                    🔐 Ride OTP (for verification at pickup)
                  </div>
                  <div style={S.otpDisplay}>{booking.otp}</div>
                  <div style={{ fontSize: 12, color: "#3b82f6", marginTop: 6 }}>
                    SMS sent to {passengerMobile}
                  </div>
                </div>

                <div
                  style={S.statusBox(
                    rideStatus === "trip_started"
                      ? "green"
                      : rideStatus === "accepted"
                        ? "blue"
                        : rideStatus === "cancelled"
                          ? "red"
                          : "amber",
                  )}
                >
                  <div style={{ fontSize: 22, marginBottom: 4 }}>
                    {rideStatus === "trip_started"
                      ? "🚀"
                      : rideStatus === "accepted"
                        ? "🛺"
                        : rideStatus === "cancelled"
                          ? "❌"
                          : "⏳"}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {rideStatus === "requested" &&
                      "Waiting for driver to accept..."}
                    {rideStatus === "accepted" &&
                      "Driver accepted! On the way to pickup"}
                    {rideStatus === "driver_on_the_way" &&
                      "Driver on the way 🛺"}
                    {rideStatus === "trip_started" &&
                      "Trip Started! ✅ OTP Verified"}
                    {rideStatus === "cancelled" && "Ride was cancelled"}
                  </div>
                  {rideStatus === "requested" && (
                    <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                      Driver-ku notification போச்சு...
                    </div>
                  )}
                </div>

                <div
                  style={{
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: "12px 14px",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: 8,
                    }}
                  >
                    Driver Details
                  </div>
                  <div style={{ fontSize: 13, color: "#4b5563" }}>
                    <div>
                      🛺 {booking.driverName} · {booking.vehicleNumber}
                    </div>
                    <div style={{ marginTop: 4 }}>📞 {booking.driverPhone}</div>
                    <div
                      style={{ marginTop: 4, fontSize: 12, color: "#9ca3af" }}
                    >
                      Ride ID: {booking.rideId}
                    </div>
                    {booking.passengerId && (
                      <div
                        style={{
                          marginTop: 6,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: booking.passengerIsNew
                            ? "#eff6ff"
                            : "#f0fdf4",
                          border: `1px solid ${booking.passengerIsNew ? "#93c5fd" : "#86efac"}`,
                          borderRadius: 20,
                          padding: "3px 10px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: booking.passengerIsNew ? "#1d4ed8" : "#16a34a",
                        }}
                      >
                        {booking.passengerIsNew
                          ? "🆕 New Passenger"
                          : "✅ Existing Passenger"}{" "}
                        · {booking.passengerId}
                      </div>
                    )}
                  </div>
                </div>

                {rideStatus !== "trip_started" &&
                  rideStatus !== "cancelled" && (
                    <button
                      style={{
                        ...S.secondaryBtn,
                        borderColor: "#fca5a5",
                        color: "#dc2626",
                        background: "#fff5f5",
                      }}
                      onClick={cancelBooking}
                    >
                      ❌ Cancel This Ride
                    </button>
                  )}
                {(rideStatus === "trip_started" ||
                  rideStatus === "cancelled") && (
                  <>
                    <button
                      style={S.primaryBtn(false)}
                      className="arb-btn"
                      onClick={handleFullReset}
                    >
                      ✅ Done
                    </button>
                    <button
                      style={{ ...S.secondaryBtn, marginTop: 8 }}
                      onClick={handleFullReset}
                    >
                      📞 New Phone Booking
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
